import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Heart, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default nav links as fallback
const defaultNavLinks = [
  { id: 'home', name: 'Home', path: '/', dropdown: [] },
  { 
    id: 'about',
    name: 'About', 
    path: '/about',
    dropdown: [
      { name: 'Welcome', path: '/welcome' },
      { name: 'Our History', path: '/history' },
      { name: 'Pastoral Message', path: '/pastoral-message' },
    ]
  },
  { id: 'ministries', name: 'Ministries', path: '/ministries', dropdown: [] },
  { id: 'sunday-school', name: 'Sunday School', path: '/sunday-school', dropdown: [] },
  { id: 'calendar', name: 'Calendar', path: '/calendar', dropdown: [] },
  { id: 'gallery', name: 'Gallery', path: '/gallery', dropdown: [] },
  { id: 'documents', name: 'Documents', path: '/documents', dropdown: [] },
  { id: 'contact', name: 'Contact', path: '/contact', dropdown: [] },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [navLinks, setNavLinks] = useState(defaultNavLinks);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle click on nav link - scroll to top if already on that page
  const handleNavClick = (e, path) => {
    if (location.pathname === path) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsOpen(false); // Close mobile menu
  };

  useEffect(() => {
    const fetchNavigation = async () => {
      try {
        const response = await fetch(`${API_URL}/api/navigation`);
        if (response.ok) {
          const data = await response.json();
          if (data.links && data.links.length > 0) {
            setNavLinks(data.links);
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch navigation:', err);
        }
      }
    };
    fetchNavigation();
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-soft" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="w-12 h-12 bg-primary-900 rounded-full flex items-center justify-center group-hover:bg-secondary transition-colors duration-300">
              <span className="text-white font-heading text-xl font-bold">G</span>
            </div>
            <div className="hidden sm:block">
              <p className="font-heading text-lg font-semibold text-primary-900 leading-tight">Geneva</p>
              <p className="text-xs text-slate-500 tracking-wide">United Methodist Church</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.id || link.name} className="relative group">
                {link.dropdown && link.dropdown.length > 0 ? (
                  <div 
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(link.id || link.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <Link
                      to={link.path}
                      onClick={(e) => handleNavClick(e, link.path)}
                      className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                        isActive(link.path) 
                          ? 'text-secondary bg-secondary/10' 
                          : 'text-slate-600 hover:text-secondary hover:bg-slate-50'
                      }`}
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                      <ChevronDown className="w-4 h-4" />
                    </Link>
                    {openDropdown === (link.id || link.name) && (
                      <div className="absolute top-full left-0 mt-1 py-2 bg-white rounded-xl shadow-lg border border-slate-100 min-w-[180px] animate-fade-in">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.name}
                            to={item.path}
                            onClick={(e) => handleNavClick(e, item.path)}
                            className="block px-4 py-2 text-slate-600 hover:text-secondary hover:bg-slate-50 transition-colors"
                            data-testid={`nav-dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    onClick={(e) => handleNavClick(e, link.path)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                      isActive(link.path) 
                        ? 'text-secondary bg-secondary/10' 
                        : 'text-slate-600 hover:text-secondary hover:bg-slate-50'
                    }`}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Give Online Button & Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="https://www.eservicepayments.com/cgi-bin/Vanco_ver3.vps?appver3=wWsk24ZWJSTZKsGd1RMKlg0BDvsSG3VIWQCPJNNxD8upkiY7JlDavDsozUE7KG0nFx2NSo8LdUKGuGuF396vbVS_o_UQftzQ0yZIGbQ1QZ4iYnTg5dP4O6rpX5QvPEWlBhHDN59kLZFffwKfYERpQmutEdaHfcR70q2N2zJmQ1Q=&ver=3"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 bg-secondary text-white px-5 py-2.5 rounded-full font-semibold hover:bg-secondary-600 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
              data-testid="nav-give-online"
            >
              <Heart className="w-4 h-4" />
              Give Online
            </a>
            
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100 animate-fade-in" data-testid="nav-mobile-menu">
            {navLinks.map((link) => (
              <div key={link.name}>
                <Link
                  to={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive(link.path) 
                      ? 'text-secondary bg-secondary/10' 
                      : 'text-slate-600 hover:text-secondary hover:bg-slate-50'
                  }`}
                  data-testid={`nav-mobile-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
                {link.dropdown && (
                  <div className="ml-4 border-l-2 border-slate-100">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={(e) => handleNavClick(e, item.path)}
                        className="block px-4 py-2 text-sm text-slate-500 hover:text-secondary transition-colors"
                        data-testid={`nav-mobile-dropdown-${item.name.toLowerCase().replace(' ', '-')}`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a
              href="https://www.eservicepayments.com/cgi-bin/Vanco_ver3.vps?appver3=wWsk24ZWJSTZKsGd1RMKlg0BDvsSG3VIWQCPJNNxD8upkiY7JlDavDsozUE7KG0nFx2NSo8LdUKGuGuF396vbVS_o_UQftzQ0yZIGbQ1QZ4iYnTg5dP4O6rpX5QvPEWlBhHDN59kLZFffwKfYERpQmutEdaHfcR70q2N2zJmQ1Q=&ver=3"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mx-4 mt-4 bg-secondary text-white px-5 py-3 rounded-full font-semibold hover:bg-secondary-600 transition-all"
              data-testid="nav-mobile-give-online"
            >
              <Heart className="w-4 h-4" />
              Give Online
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};
